import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import { ButtonIconSeeker, IconSeeker } from '@ui-seeker/core'
import { copyToClipboard, postExcel } from '@ui-seeker/utils'

import { AppCardTitle, AppInfoButton, AppNote, AppPillToggle, AppResultsSkeleton, NoDataFound, headerIconButtonSx, secondaryButtonSx } from '@components'
import { BACKEND_APIS, BI_ROUTES, USER_ROUTES } from '@constants'
import { BUSINESS_LOGISTICS_ROUTES } from '@constants/routes'
import { useToast } from '@hooks'
import { slaveId, userData } from '@signals/user'
import { dsColorNew } from '@palette'
import PageWrapper from 'Pages/@PageWrapper'
import { pickCurrency } from '@utils/currencyDefault'

import ViewBand, { CapturedField, LorField, OfficeField, ViewToggle } from '../../@components/ViewBand'
import CarsField from '../../@components/CarsField'
import MarketBar from '../../@components/MarketBar'
import IdentityChip from '../../@components/IdentityChip'
import useDashboardOptions from '../../@hooks/useDashboardOptions'
import useDashboardData from '../../@hooks/useDashboardData'
import { buildDateCurve } from '../../@services/curve'
import { addDays, fmtDay, fmtRange, fromIso } from '../../@services/dates'
import { explorerForm, explorerUrl } from '../../@services/explorerLink'
import { carsLabels, carsOn, shortList } from '../../@services/cars'
import { marketContext, providerOptions, scopeSubtitle, tierCoverage, visibleProviderIds } from '../../@services/market'
import { DEFAULT_WHEN, pickupIso } from './@services/pickup'
import { parseView, peNoticeKey, resolveView, storageKey, viewToParams, viewToStored } from './@services/view'
import { bodyKey, curveBody, dayBody, providerBody, weeklyBody, weeklyExcelBody } from './@services/request'
import { withCleanProviderNames } from '../../@services/providerName'
import { isPending } from '../../@services/latestOnly'
import { last7, positionAt, whoMovedFirst } from './@services/cards'
import { buildWeekly } from './@services/weekly'
import useCachedResult from './@hooks/useCachedResult'
import PickupField from './@components/PickupField'
import DateCurve from './@components/DateCurve'
import WeeklyTable, { WeeklyLegend } from './@components/WeeklyTable'
import { Last7Card, PositionCard, WhoMovedFirstCard, cardSx } from './@components/Cards'
import { CaptureWeeksField, CurrencyField, OfficesField, PickupDatesField, SitesField, TransmissionField } from './@components/WeeklyFields'
import { DayDetailDialog, ProviderDetailDialog } from './@components/DetailDialogs'


const STAT_WORD = { min: 'Min', avg: 'Avg', max: 'Max' }

// the veil of Season vs last year (index.jsx): what is painted stays, greyed, while the new answer comes
const UpdatingVeil = () => <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 7.5, zIndex: 6, borderRadius: '16px' }}>
	<Box component="span" role="status" sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '999px', px: 1.75, py: 0.75, fontSize: 13, color: dsColorNew.textMuted, boxShadow: '0 12px 32px rgba(12,26,62,.12)' }}>Updating…</Box>
</Box>
const EXTRA_ROW = ['sites', 'transmission', 'currency']

const readStored = uid => {
	try { return JSON.parse(localStorage.getItem(storageKey(uid)) || 'null') } catch { return null }
}
const toastBody = (title, lines = []) => <Box>
	<Box sx={{ fontWeight: 500, color: dsColorNew.textTitle }}>{title}</Box>
	{lines.map((l, i) => <Box key={i} sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.25 }}>{l}</Box>)}
</Box>

function SameDateCheck({ checked, onChange }) {
	return <Box sx={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center' }}>
		<Button variant="text" role="checkbox" aria-checked={checked} onClick={onChange}
			sx={{ textTransform: 'none', color: dsColorNew.textDark, fontSize: 13, fontWeight: 400, gap: 1, px: 1 }}
			startIcon={<Box component="span" sx={{
				width: 16, height: 16, borderRadius: '4px', border: `1.5px solid ${checked ? dsColorNew.appBlue : '#c3c9d4'}`,
				backgroundColor: checked ? dsColorNew.appBlue : dsColorNew.white, color: dsColorNew.white, fontSize: 11,
				display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			}}>{checked ? '✓' : ''}</Box>}>
			Same date, not same weekday
		</Button>
		<AppInfoButton title="Same weekday or same date" tooltip="Same weekday or same date">
			<p><b>By default</b> the dotted line is the same weekday last year: Saturday 31 Oct 2026 with Saturday 1 Nov 2025.</p>
			<p><b>Same date</b> compares with the same calendar date, whatever the weekday.</p>
		</AppInfoButton>
	</Box>
}

/**
 * BI dashboard "Price curve of a date" (CU-869fmjzrg): pick one pick-up date (or week) and see how every provider moved
 * its price as it came closer — who moved first, how much each changed in the last 7 days, where you stand — and,
 * "Week by week", the table that was Pricing Environment. Opens with an answer; every change of the band asks again;
 * Tier / Providers, Min / Avg / Max and the lines shown never do.
 */
export default function PriceCurvePage() {
	const { showToast } = useToast()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const uid = slaveId.value || userData.value?.uid
	const today = useMemo(() => new Date(), [])
	const options = useDashboardOptions()

	const [view, setView] = useState(null)
	const [tierOptions, setTierOptions] = useState(null)
	const [providerDetail, setProviderDetail] = useState(null)
	const [day, setDay] = useState(null)

	// CA 4: the URL, then the last view, then the last office used in BI, then the first office BI offers
	useEffect(() => {
		if (options.loading || view) return
		const stored = readStored(uid)
		const v = resolveView(searchParams, stored)
		const ids = options.offices.map(o => Number(o.value))
		const candidates = [parseView(searchParams).office, parseView(stored || {}).office, options.lastOffice, ids[0]]
		v.office = candidates.map(Number).find(id => id && ids.includes(id)) || null
		const lors = options.lors.map(o => o.value)
		if (lors.length && !lors.includes(v.lor)) v.lor = lors.includes(7) ? 7 : lors[0]
		if (!v.offices.length && v.office) v.offices = [v.office]
		if (!v.dates.length) v.dates = [pickupIso({ pick: 'date', when: DEFAULT_WHEN.date }, today)]
		setView(v)
		// CA 22: arriving from the old Pricing Environment address, once per user
		if (searchParams.get('from') === 'pricing-environment') {
			try {
				if (!localStorage.getItem(peNoticeKey(uid))) {
					localStorage.setItem(peNoticeKey(uid), '1')
					showToast.info(toastBody('Pricing Environment is now here', [
						'Same table, in “Week by week”. Your offices, dates and weeks are in the band above.',
						'Colours changed: green is now cheaper or down, red dearer or up.',
					]))
				}
			} catch {}
		}
	}, [options.loading])

	useEffect(() => {
		if (!view) return
		setSearchParams(viewToParams(view), { replace: true })
		try { localStorage.setItem(storageKey(uid), JSON.stringify(viewToStored(view))) } catch {}
	}, [view && viewToParams(view)])

	const update = patch => setView(v => ({ ...v, ...patch }))
	const currency = pickCurrency(options.currencies, null, { fallbackToFirst: true })
	const isCurve = view?.view !== 'weekly'

	// data: one request per view; the other view keeps its own selection
	const body = view && isCurve ? curveBody({ view, currency: currency?.value, today }) : null
	const curveState = useDashboardData(body)
	const wBody = view && !isCurve ? weeklyBody({ view: { ...view, cur: view.cur ?? currency?.value ?? null }, currency: currency?.value }) : null
	const weeklyState = useDashboardData(wBody)
	// never a logo URL as a provider name (Curro's review; `MASTER_PROVIDERS.name` upstream)
	const cData = useMemo(() => withCleanProviderNames(curveState.data), [curveState.data])
	const wData = useMemo(() => withCleanProviderNames(weeklyState.data), [weeklyState.data])
	const data = isCurve ? cData : wData

	// "Updating…" from the moment the view stops matching what is painted (the 400 ms wait included, after an error
	// too) until the answer to this very view arrives; a late answer to the previous view never ends it (QA)
	const curvePending = isPending({ key: bodyKey(body), dataKey: curveState.dataKey, errorKey: curveState.errorKey, loading: curveState.loading })
	const weeklyPending = isPending({ key: bodyKey(wBody), dataKey: weeklyState.dataKey, errorKey: weeklyState.errorKey, loading: weeklyState.loading })

	// Tier / Providers: who counts as the market, on what was received (decision 8 of CU-869fmhmx8)
	const tier = view?.tier && tierOptions ? tierOptions.find(t => Number(t.value) === Number(view.tier)) || null : null
	const tierName = tier?.label || null
	const tierProviders = tier ? (tier.providers || []).map(Number) : null
	const picked = view?.tier ? [] : view?.providers || []
	const marketActive = !!tier || !!picked.length
	const visible = useMemo(() => (data && marketActive
		? visibleProviderIds({ providers: data.providers || [], identity: data.identity, tierProviders, picked })
		: null), [data, `${tier?.value ?? ''}|${picked.join(',')}`])
	const coverage = t => tierCoverage({ providers: data?.providers || [], identity: data?.identity, tierProviders: (t?.providers || []).map(Number) })

	const curve = useMemo(() => (isCurve && cData ? buildDateCurve(cData, { stat: view?.stat, visible }) : null), [isCurve, cData, view?.stat, visible])
	const answers = useMemo(() => (curve && !curve.empty ? { last7: last7(curve), moved: whoMovedFirst(curve), position: positionAt(curve) } : null), [curve])
	const table = useMemo(() => (!isCurve && wData && !wData.noResults ? buildWeekly(wData, { stat: view?.stat, visible }) : null), [isCurve, wData, view?.stat, visible])

	const args = view ? { view, currency: currency?.value, today } : null
	const providerState = useCachedResult(providerDetail && args ? providerBody(args, providerDetail.id, providerDetail.split) : null)
	const dayState = useCachedResult(day !== null && args ? dayBody(args, day) : null)

	const code = data?.currency?.code || currency?.label || 'EUR'
	const officeName = options.offices.find(o => Number(o.value) === Number(view?.office))?.label || ''
	const identity = data?.identity || null
	const brand = (data?.providers || []).find(p => p.own === 'primary')?.name
	const pickupDates = cData?.pickupDates || []
	const past = isCurve && !!pickupDates.length && fromIso(pickupDates.at(-1)) < addDays(today, -1)
	const carsText = view ? shortList(carsLabels(view, { groups: data?.cars?.groups || [] })) : ''

	useEffect(() => {
		if (tierOptions && view?.tier && !tierOptions.some(t => Number(t.value) === Number(view.tier))) update({ tier: null })
	}, [tierOptions, view?.tier])

	const setTier = id => update({ tier: id, providers: [] })
	const setProviders = ids => update({ tier: null, providers: ids })
	const clearMarket = () => update({ tier: null, providers: [] })

	const goSettings = () => {
		showToast.info(toastBody('Opening Settings › Account', ['Your brand is the first one in Identity.']))
		navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=account`)
	}
	const goAcrissGroups = () => navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=acriss_group`)
	const goWeekly = () => update({ view: 'weekly', okind: 'offices', offices: view.office ? [view.office] : view.offices, dates: pickupDates.length ? pickupDates : view.dates })
	const goCurve = (office, date) => update({ view: 'curve', office: office || view.office, pick: 'date', when: 'fixed', n: null, date })

	const copyLink = () => {
		try {
			copyToClipboard(`${window.location.origin}${BUSINESS_LOGISTICS_ROUTES.PRICE_CURVE_PATH}?${viewToParams(view)}`)
			const keeps = !isCurve ? 'It keeps these offices, dates and weeks.'
				: view.when === 'fixed' ? 'It keeps this fixed date.' : 'It keeps the relative pick-up: opened another day, the date moves with it.'
			const filters = [carsOn(view) ? carsText : null, marketActive ? (tierName || `${picked.length} providers`) : null].filter(Boolean)
			showToast.success(toastBody('Link copied', [keeps, ...(filters.length ? [`With its filters: ${filters.join(' · ')}.`] : [])]))
		} catch {
			showToast.error('Could not copy to clipboard')
		}
	}

	const providerIdsShown = () => (marketActive && data ? [...visibleProviderIds({ providers: data.providers || [], identity: data.identity, tierProviders, picked })] : null)
	const openExplorer = (kind = 'curve', extra = {}) => {
		const d = cData
		if (!view?.office || !d?.pickupDates?.length) return
		const dates = d.pickupDates
		const capture = extra.capture || (d.window ? { from: d.window.from, to: d.window.to } : { from: addDays(fromIso(dates[0]), -d.days), to: addDays(today, -1) })
		const form = explorerForm({
			kind, view: { ...view, cmp: 'ly' }, pickup: { from: dates[0], to: dates.at(-1) }, capture, currency: currency?.value,
			columnDates: dates, resolvedCars: d.cars, providerIds: extra.providerIds || providerIdsShown(),
		})
		showToast.info(toastBody(`Opening ${BI_ROUTES.TITLE}`, [`${kind === 'curve' ? 'Provider × booking date' : 'Provider × Acriss'} · ${officeName} · ${view.lor} days`, 'The Excel of this data is there.']))
		window.open(explorerUrl(form, BI_ROUTES.PATH), '_blank', 'noopener')
	}
	const openDayInExplorer = () => {
		const pairs = dayState.data?.pairs || []
		if (!pairs.length) return
		openExplorer('day', { capture: { from: pairs[0].captured, to: pairs.at(-1).captured } })
	}

	const downloadExcel = () => {
		if (!wBody) return
		postExcel(`${BACKEND_APIS.DASHBOARD}/excel`, weeklyExcelBody(wBody, view.stat, providerIdsShown()))
		showToast.info(toastBody('Excel downloading', ['Office, provider, pick-up date, week, price and change, with the filters on top.']))
	}

	const actions = <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<AppInfoButton title="About this screen" tooltip="About this screen">
			<p>Pick one pick-up date (or one week) and see how every provider moved its price as the date came closer: who moved first, how much each one changed in the last 7 days and where you stand today.</p>
			<p><b>Week by week</b> is the table that was called Pricing Environment: each provider's price by capture week and its change against the week before.</p>
			<p>Same prices as {BI_ROUTES.TITLE}: the ones your company has captured. Green means cheaper or down, red dearer or up.</p>
		</AppInfoButton>
		<ButtonIconSeeker icon="link" iconWeight="regular" tooltip="Copy link to this view" aria-label="Copy link to this view" sx={headerIconButtonSx} disabled={!view} onClick={copyLink}/>
		<Button variant="outlined" onClick={() => openExplorer('curve')} disabled={!isCurve || !cData?.pickupDates?.length} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>}
			sx={{ ...secondaryButtonSx, height: 40, color: dsColorNew.webBlue500, fontWeight: 600 }}>
			Open in {BI_ROUTES.TITLE}
		</Button>
	</Box>

	const scope = marketActive && data ? scopeSubtitle({
		shown: isCurve ? (curve?.others.length ?? 0) : (table?.total ?? 0), all: (data.providers || []).filter(p => !(identity && p.own === 'primary')).length,
		competitors: isCurve ? curve?.competitors ?? 0 : table?.competitors ?? 0, identity, tierName, coverage: tier ? coverage(tier) : null,
	}) : null
	const scopeLine = scope ? <>{scope.text} · <Button variant="text" size="small" onClick={clearMarket} sx={{ textTransform: 'none', p: '0 4px', minWidth: 0, fontSize: 12.5, verticalAlign: 'baseline', lineHeight: 'inherit' }}>{scope.action}</Button></> : null

	const ready = !!view
	const noOffices = ready && !options.loading && !options.offices.length

	const fields = !ready ? null : isCurve ? {
		office: <OfficeField value={view.office} options={options.offices} onChange={office => update({ office })}/>,
		lor: <LorField value={view.lor} options={options.lors} onChange={lor => update({ lor })}/>,
		cars: <CarsField view={view} resolved={data?.cars} onChange={cars => update({ ...cars })} onOpenSettings={goAcrissGroups}/>,
		pickup: <PickupField view={view} pickupDates={pickupDates} today={today} onChange={patch => update(patch)}/>,
		captured: <CapturedField until={cData?.window ? `${fmtRange(fromIso(cData.window.from), fromIso(cData.window.to))} · ${cData.days} days` : null}/>,
	} : {
		office: <OfficesField view={view} offices={options.offices} onChange={update}/>,
		lor: <LorField value={view.lor} options={options.lors} onChange={lor => update({ lor })}/>,
		cars: <CarsField view={view} resolved={data?.cars} onChange={cars => update({ ...cars })} onOpenSettings={goAcrissGroups}/>,
		pickup: <PickupDatesField dates={view.dates} onChange={dates => update({ dates })}/>,
		captured: <CaptureWeeksField weeks={view.weeks} options={wData?.weekOptions} onChange={weeks => update({ weeks })}/>,
		sites: <SitesField view={view} onChange={update}/>,
		transmission: <TransmissionField value={view.trans} onChange={trans => update({ trans })}/>,
		currency: <CurrencyField value={view.cur ?? currency?.value} options={options.currencies} onChange={cur => update({ cur })}/>,
	}

	const contextLine = ready && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', fontSize: 13, color: dsColorNew.textMuted }}>
		<Box component="span">
			{isCurve ? <>
				<b style={{ color: dsColorNew.textTitle }}>{officeName}</b> · {view.lor}-day rentals
				{carsOn(view) && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{carsText}</span></>}
				{!!pickupDates.length && <> · pick-up <b style={{ color: dsColorNew.textTitle }}>{pickupDates.length > 1 ? fmtRange(fromIso(pickupDates[0]), fromIso(pickupDates.at(-1))) : fmtDay(fromIso(pickupDates[0]))}</b></>}
				{' '}· {STAT_WORD[view.stat]} per provider · vs last year, {view.same ? 'same date' : 'same weekday'}
			</> : <>
				{view.offices.length} {view.okind === 'groups' ? `office group${view.offices.length === 1 ? '' : 's'}` : `office${view.offices.length === 1 ? '' : 's'}`} · {view.dates.length} pick-up date{view.dates.length === 1 ? '' : 's'}
				{!!wData?.weeks?.length && <> · capture weeks {wData.weeks[0].label}–{wData.weeks.at(-1).label}</>} · {code}
				{view.price === 'daily' && ' · daily price'}
			</>}
			{marketActive && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{marketContext(tierName)}</span></>}
		</Box>
		<Box sx={{ flex: 1 }}/>
		{!!data && (identity && brand
			? <IdentityChip brand={brand} onChange={goSettings}/>
			: <Box component="span">No brand set · <Button variant="text" size="small" onClick={goSettings} sx={{ textTransform: 'none', p: '0 4px', minWidth: 0 }}>Set it in Settings › Account</Button></Box>)}
	</Box>

	const loadError = (state, long) => (state.error?.status === 400
		? <AppNote severity="error">{long}</AppNote>
		: <AppNote severity="error"><b>Could not load the prices.</b> Your view is kept.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={state.retry} sx={secondaryButtonSx}>Try again</Button></Box></AppNote>)

	return <PageWrapper
		head={`DS Rentacar | ${BUSINESS_LOGISTICS_ROUTES.PRICE_CURVE_TITLE}`}
		title={BUSINESS_LOGISTICS_ROUTES.PRICE_CURVE_TITLE}
		subtitle={BUSINESS_LOGISTICS_ROUTES.PRICE_CURVE_SUBTITLE}
		isEmpty={false}
		actions={actions}
	>
		{ready && <ViewBand
			toggles={<>
				<ViewToggle label="View">
					<AppPillToggle aria-label="View" value={view.view} onChange={v => (v === 'weekly' ? goWeekly() : update({ view: 'curve' }))}
						options={[{ value: 'curve', label: 'Price curve' }, { value: 'weekly', label: 'Week by week' }]}/>
				</ViewToggle>
				{isCurve && <ViewToggle label="Pick-up">
					<AppPillToggle aria-label="Pick-up" value={view.pick} onChange={pick => update({ pick, when: DEFAULT_WHEN[pick], n: null, date: null })}
						options={[{ value: 'date', label: 'Date' }, { value: 'week', label: 'Week' }]}/>
				</ViewToggle>}
				{isCurve && <SameDateCheck checked={view.same} onChange={() => update({ same: !view.same })}/>}
			</>}
			togglesRight={<>
				{!isCurve && <ViewToggle label="Price">
					<AppPillToggle size="small" aria-label="Price" value={view.price} onChange={price => update({ price })}
						options={[{ value: 'total', label: 'Total' }, { value: 'daily', label: 'Daily' }]}/>
				</ViewToggle>}
				<ViewToggle label="Price per provider">
					<AppPillToggle size="small" aria-label="Price per provider" value={view.stat} onChange={stat => update({ stat })}
						options={[{ value: 'min', label: 'Min' }, { value: 'avg', label: 'Avg' }, { value: 'max', label: 'Max' }]}/>
				</ViewToggle>
			</>}
			fields={fields}
			extraRow={isCurve ? null : EXTRA_ROW}
		/>}

		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
			{contextLine}

			{!!data && !data.noResults && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
				<MarketBar
					tier={tier ? Number(tier.value) : null} onTierOptions={setTierOptions} onTier={setTier}
					options={providerOptions({ providers: data.providers || [], identity })}
					shown={tier ? coverage(tier).ids : picked.filter(id => (data.providers || []).some(p => Number(p.id) === Number(id)))}
					coverage={coverage} active={marketActive} onProviders={setProviders}
				/>
				{marketActive && <Button variant="text" onClick={clearMarket} sx={{ textTransform: 'none', color: dsColorNew.textMuted, height: 40 }}>Clear filters</Button>}
			</Box>}

			{noOffices && <NoDataFound title="No offices to show" text="Business Intelligence has no offices for your account yet."/>}

			{!noOffices && isCurve && <>
				<Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
				{!!answers && !curve.beyond && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
					<Last7Card rows={answers.last7} curve={curve} code={code} past={past} onOpen={id => setProviderDetail({ id, split: 'category' })}/>
					<WhoMovedFirstCard moved={answers.moved} curve={curve} code={code} onOpen={id => setProviderDetail({ id, split: 'category' })}/>
					<PositionCard pos={answers.position} code={code} past={past} identity={identity} onSettings={goSettings}/>
				</Box>}
				{curveState.error && !cData ? loadError(curveState, 'This view is too long.') : <DateCurve
					curve={curve} curveId={curveState.dataKey} loading={(curveState.loading && !cData) || !ready} error={curveState.error} onRetry={curveState.retry}
					stat={view?.stat} lor={view?.lor} code={code} today={today} ly={!!view?.ly} onLy={() => update({ ly: !view.ly })}
					ring={answers?.moved.first[0] ? { key: answers.moved.first[0].line.key, d: answers.moved.first[0].d } : null}
					firstMove={answers?.moved.first[0] ? { key: answers.moved.first[0].line.key, d: answers.moved.first[0].d, pct: answers.moved.first[0].pct } : null}
					scope={scopeLine} onDay={setDay} onWeekly={goWeekly} onExplorer={() => openExplorer('curve')}
				/>}
				{curvePending && !!cData && <UpdatingVeil/>}
				</Box>
			</>}

			{!noOffices && !isCurve && <Box component="section" sx={cardSx}>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
					<AppCardTitle icon="table" title="Prices by capture week" subtitle={weeklyState.loading && !wData ? 'Loading…'
						: table ? <>{table.total} {identity ? 'other providers' : 'providers'} · change against the week before in the selection{scopeLine ? <> · {scopeLine}</> : null}</> : ''} sx={{ minWidth: 0, flex: 1 }}>
						<AppInfoButton title="How to read this table" tooltip="How to read this table">
							<p><b>Each cell</b> is the provider's price for that pick-up date in that capture week (Min, Avg or Max of the prices captured), and its change against the previous week of your selection.</p>
							<p><b>L</b> is the cheapest and <b>H</b> the dearest of the providers shown. The colours go the other way round from the old Pricing Environment: green is now cheaper or down, red dearer or up.</p>
							<p>Weeks run Sunday to Saturday.</p>
						</AppInfoButton>
					</AppCardTitle>
					<ButtonIconSeeker icon="file-excel" iconWeight="regular" tooltip="Download Excel" aria-label="Download Excel" sx={headerIconButtonSx} disabled={!table} onClick={downloadExcel}/>
				</Box>
				<Box sx={{ position: 'relative' }}>
					{weeklyState.error && !weeklyState.loading ? loadError(weeklyState, 'This view is too long: choose fewer offices, dates or weeks.')
						: !wData ? <AppResultsSkeleton columns={8}/>
							: wData.noResults ? <NoDataFound title="No prices for these offices and dates in these weeks" text="Try other capture weeks or other offices."/>
								: <WeeklyTable table={table} code={code} onCurve={goCurve}/>}
					{weeklyPending && !!wData && <UpdatingVeil/>}
				</Box>
				{!!table && <WeeklyLegend daily={table.daily}/>}
			</Box>}
		</Box>

		{!!providerDetail && <ProviderDetailDialog open name={(data?.providers || []).find(p => Number(p.id) === Number(providerDetail.id))?.name || ''}
			split={providerDetail.split} onSplit={split => setProviderDetail(p => ({ ...p, split }))} state={providerState} stat={view.stat} code={code} today={today}
			onExplorer={() => openExplorer('provider', { providerIds: [providerDetail.id] })} onClose={() => setProviderDetail(null)}/>}
		{day !== null && <DayDetailDialog open d={day} days={curve?.days || 60} pickupDates={pickupDates} state={dayState} stat={view.stat} code={code} identity={identity}
			onDay={setDay} onExplorer={openDayInExplorer} onClose={() => setDay(null)}/>}
	</PageWrapper>
}
